/**
 * The rail: 56 px of icons, labels on hover, pinned open to 200 px with a click. "Log a ticket"
 * is always first. Below the links, the team's Dialpad status (names show when the rail is open).
 */
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Inbox, Kanban, LayoutDashboard, PanelLeft, Phone, Plus, Search, Settings, Users } from 'lucide-react';
import { useActor } from '../../lib/auth';
import { openShiftNote } from '../shift/shift-store';
import { TeamList } from './team-list';
import { draftsQuery } from '../../lib/queries';
import { Mark } from '../ui/mark';
import { useLive } from '../../lib/live';
import { setPrefs, usePrefs } from '../../lib/prefs';

interface Item {
  to: string;
  label: string;
  icon: typeof Search;
  kbd?: string;
}

const NAV: Item[] = [
  // Overview first (Jeff, 2026-10-25): the board in figures is where a shift starts.
  { to: '/overview', label: 'Overview', icon: LayoutDashboard },
  { to: '/', label: 'Look up', icon: Search, kbd: '/' },
  { to: '/board', label: 'Board', icon: Kanban, kbd: 'B' },
  { to: '/calls', label: 'Calls', icon: Phone },
  { to: '/cleanup', label: 'Cleanup', icon: Inbox },
];

export function Rail({ onLogTicket }: { onLogTicket: () => void }) {
  const { railPinned, hideOffDuty } = usePrefs();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const open = railPinned;
  const pending = useQuery({ ...draftsQuery(1), refetchInterval: 60_000 }).data?.total;
  const live = useLive();
  const actor = useActor();

  return (
    <nav
      aria-label="Desk"
      className={`group/rail flex h-full flex-col border-r border-line bg-surface transition-[width] duration-150 ${open ? 'w-[200px]' : 'w-14 hover:w-[200px]'}`}
    >
      <div className="flex h-12 items-center gap-3 overflow-hidden px-3">
        <Mark className="size-8" />
        <span className={`truncate text-emphasis font-medium ${open ? '' : 'opacity-0 group-hover/rail:opacity-100'} transition-opacity`}>Support Center</span>
      </div>

      <div className="px-2 pt-1">
        <button
          type="button"
          onClick={onLogTicket}
          className="flex h-9 w-full items-center gap-3 overflow-hidden rounded-control bg-ink px-2.5 text-canvas hover:opacity-90"
        >
          <Plus className="size-4 shrink-0" />
          <span className={`flex flex-1 items-center justify-between whitespace-nowrap text-body font-medium ${open ? '' : 'opacity-0 group-hover/rail:opacity-100'} transition-opacity`}>
            Log a ticket <kbd className="border-canvas/30 bg-transparent text-canvas/70">C</kbd>
          </span>
        </button>
      </div>

      <ul className="mt-3 flex flex-col gap-0.5 px-2">
        {NAV.map((n) => {
          const Icon = n.icon;
          const active = path === n.to || (n.to !== '/' && path.startsWith(n.to));
          const inner = (
            <>
              <Icon className="size-4 shrink-0" />
              <span className={`flex flex-1 items-center justify-between whitespace-nowrap ${open ? '' : 'opacity-0 group-hover/rail:opacity-100'} transition-opacity`}>
                {n.label}
                {n.kbd ? <kbd>{n.kbd}</kbd> : n.to === '/cleanup' && pending ? <span className="text-hint text-ink-3">{pending}</span> : null}
              </span>
            </>
          );
          const cls = `flex h-9 items-center gap-3 overflow-hidden rounded-control px-2.5 text-body ${active ? 'bg-surface-2 text-ink' : 'text-ink-2 hover:bg-surface-2 hover:text-ink'}`;
          return (
            <li key={n.to}>
              <Link to={n.to} className={cls}>
                {inner}
              </Link>
            </li>
          );
        })}
      </ul>

      {live.team && (
        <section aria-label="Team" className="mt-4 min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2">
          <p className="flex items-center gap-3 px-2.5 py-1 text-hint text-ink-3" title={`${String(live.team.filter((m) => m.onDuty).length)} on duty`}>
            <Users className="size-4 shrink-0" aria-hidden="true" />
            <span className={`whitespace-nowrap ${open ? '' : 'opacity-0 group-hover/rail:opacity-100'} transition-opacity`}>{`Team, ${String(live.team.filter((m) => m.onDuty).length)} on duty`}</span>
          </p>
          <div className={open ? 'mt-1' : 'mt-1 hidden group-hover/rail:block'}>
            <TeamList
              team={live.team}
              viewerEmail={actor?.email ?? ''}
              onHandOff={openShiftNote}
              hideOffDuty={hideOffDuty}
              onHideOffDuty={(hide) => {
                setPrefs({ hideOffDuty: hide });
              }}
            />
          </div>
        </section>
      )}

      <div className="mt-auto flex flex-col gap-2 px-2 pb-3">
        <SettingsLink path={path} open={open} />
        <button
          type="button"
          onClick={() => {
            setPrefs({ railPinned: !railPinned });
          }}
          className="flex h-8 items-center gap-3 overflow-hidden rounded-control px-2.5 text-ink-3 hover:bg-surface-2 hover:text-ink"
          aria-label={railPinned ? 'Collapse the rail' : 'Keep the rail open'}
        >
          <PanelLeft className="size-4 shrink-0" />
          <span className={`whitespace-nowrap text-hint ${open ? '' : 'opacity-0 group-hover/rail:opacity-100'} transition-opacity`}>{railPinned ? 'Collapse' : 'Keep open'}</span>
        </button>
      </div>
    </nav>
  );
}

/** Settings, at the foot of the rail above Keep open; current anywhere under /settings. */
export function SettingsLink({ path, open }: { path: string; open: boolean }) {
  const active = path === '/settings' || path.startsWith('/settings/');
  return (
    <Link
      to="/settings"
      aria-current={active ? 'page' : undefined}
      className={`flex h-8 items-center gap-3 overflow-hidden rounded-control px-2.5 ${active ? 'bg-surface-2 text-ink' : 'text-ink-3 hover:bg-surface-2 hover:text-ink'}`}
    >
      <Settings className="size-4 shrink-0" aria-hidden="true" />
      <span className={`whitespace-nowrap text-hint ${open ? '' : 'opacity-0 group-hover/rail:opacity-100'} transition-opacity`}>Settings</span>
    </Link>
  );
}
